import { useLingui } from "@lingui/react/macro"
import type {
  ContactDrawerActivityItem,
  ContactDrawerActivityScope,
  ContactDrawerActiveScope,
} from "@/types/v2/conversation"

// The contact activity "Scope" pills — All deals /
// per-deal / Contact-only. Shared by the contact slideover
// (`ContactEntryBody`) and the contact show page so the two
// surfaces stay identical. The caller owns the navigation:
// `onSelect` gets the picked scope and decides how to write
// it (the slideover rewrites the `?drawer=` trail; the show
// page rewrites the page URL's scope params).
export function ContactActivityScopeRow({
  scopes,
  active,
  onSelect,
  testId = "contact-scope-row",
}: {
  scopes: ContactDrawerActivityScope[]
  active: ContactDrawerActiveScope
  onSelect: (scope: { kind: string; dealId: number | null }) => void
  testId?: string
}) {
  const { t } = useLingui()
  return (
    <div
      data-testid={testId}
      className="flex items-center gap-1.5 flex-wrap"
    >
      <span className="tracking-[0.06em] uppercase font-semibold text-[#7A746B] text-[11px] leading-3 mr-1">
        {t`Scope`}
      </span>
      {scopes.map((scope) => (
        <ScopePill
          key={`${scope.kind}-${scope.dealId ?? "none"}`}
          scope={scope}
          isActive={isActiveScope(active, scope)}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}

function ScopePill({
  scope,
  isActive,
  onSelect,
}: {
  scope: ContactDrawerActivityScope
  isActive: boolean
  onSelect: (scope: { kind: string; dealId: number | null }) => void
}) {
  const base =
    "inline-flex items-center gap-1.5 rounded-full py-1 px-2.5 text-[11px] leading-[14px] cursor-pointer transition-colors"
  const tone = isActive
    ? "bg-[#1E1B17] text-[#F0EDE6] font-medium"
    : "bg-transparent border border-[#E5DFD3] text-[#5C544A] hover:bg-[#F0EDE6]"

  return (
    <button
      type="button"
      onClick={() => onSelect({ kind: scope.kind, dealId: scope.dealId })}
      data-testid={`contact-scope-${scope.kind}-${scope.dealId ?? "none"}`}
      aria-pressed={isActive}
      className={`${base} ${tone}`}
    >
      {scope.kind === "deal" ? <ScopeDot /> : null}
      <span>{scope.label}</span>
      <span className={isActive ? "text-[#C9C2B6]" : "text-[#9A938A]"}>
        {scope.count}
      </span>
    </button>
  )
}

function ScopeDot() {
  return (
    <span
      aria-hidden="true"
      className="size-1.5 rounded-full bg-[#B86A2D]"
    />
  )
}

function isActiveScope(
  active: ContactDrawerActiveScope,
  scope: ContactDrawerActivityScope,
): boolean {
  if (scope.kind !== active.kind) return false
  if (scope.kind === "deal") return scope.dealId === active.dealId
  return true
}

// Filter activity items down to the active scope. Shared so
// the slideover and show page filter identically: "all"
// shows everything, "contact_only" shows items with no deal,
// a deal scope shows only that deal's items.
export function filterContactActivityByScope(
  items: ContactDrawerActivityItem[],
  active: ContactDrawerActiveScope,
): ContactDrawerActivityItem[] {
  if (active.kind === "all") return items
  if (active.kind === "contact_only") {
    return items.filter((i) => i.dealId == null)
  }
  return items.filter((i) => i.dealId === active.dealId)
}
